import {
  Box,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "../../../assets/css/steps.css";

const Step_0_Delegation = ({
  delegation,
  setDelegation,
  errors,
  handleEmailFormat,
  emailError,
  confirmEmail,
  setConfirmEmail,
  confirmTelephone,
  setConfirmTelephone,
  setErrors,
}) => {
  const url = window.location.pathname;
  const isDelegationEdit = url.includes("/delegation/");

  const [initialized, setInitialized] = useState(false);

  const [emailConfirmError, setEmailConfirmError] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);

  const [phoneError, setPhoneError] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);

  const [phoneFocused, setPhoneFocused] = useState(false);

  const normalize = (num = "") =>
    ("+" + num.replace(/\s+/g, "").replace(/[^\d]/g, "")).replace(/\+\+/, "+");

  useEffect(() => {
    if (delegation.email && confirmEmail) {
      setEmailConfirmError(delegation.email !== confirmEmail);
    }
  }, [delegation.email, confirmEmail]);

  useEffect(() => {
    if (!initialized && url.includes("/delegation/")) {
      if (delegation.email) setConfirmEmail(delegation.email);
      if (delegation.telephone) setConfirmTelephone(delegation.telephone);
      setInitialized(true);
    }
  }, [initialized]);

  useEffect(() => {
    const phone1 = normalize(delegation.telephone || "").replace("+", "");
    const phone2 = normalize(confirmTelephone || "").replace("+", "");

    const isLengthError = phone1.length > 0 && phone1.length <= 4;

    const isMismatchError =
      phoneTouched &&
      phone1.length > 0 &&
      phone2.length > 0 &&
      phone1 !== phone2;

    const isValid = phone1 === phone2 && phone1.length > 4;

    if (isValid) {
      setPhoneError(false);
    } else if (isMismatchError) {
      setPhoneError(true);
    } else if (!phoneFocused && isLengthError) {
      setPhoneError(true);
    }

    if (
      phone1.length > 0 &&
      phone1.length <= 4 &&
      phone1 === phone2 &&
      !phoneFocused
    ) {
      setPhoneError(true);
    }

    if (phone1.length === 0 && !phoneTouched) {
      setPhoneError(false);
    }
  }, [delegation.telephone, confirmTelephone, phoneFocused, phoneTouched]);

  const handlePhoneChange = (value) => {
    if (!phoneTouched) setPhoneTouched(true);
    removeFieldError("telephone");

    if (phoneError) setPhoneError(false);

    setDelegation((prev) => ({
      ...prev,
      telephone: value,
    }));
  };

  if (!delegation) return null;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setDelegation((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const emailInputError = emailError || errors.includes("email");
  const confirmEmailInputError =
    (emailTouched && emailConfirmError) || errors.includes("confirm_email");

  const phoneInputErrorStyle = (isConfirmField = false) => {
    const hasError = isConfirmField
      ? (phoneTouched && phoneError) || errors.includes("confirm_telephone")
      : phoneError || errors.includes("telephone");

    return {
      width: "100%",
      border: hasError ? "1px solid #D32F2F" : "1px solid rgba(0,0,0,0.23)",
      borderRadius: "4px",
      padding: "16.5px 14px",
    };
  };

  const removeFieldError = (field) => {
    if (errors.includes(field)) {
      const newErrors = errors.filter((e) => e !== field);
      setErrors(newErrors);
    }
  };

  return (
    <Grid container>
      <Grid item xs={12} className="container-steps">
        <Typography className="title-steps">
          Indica quale tipo di azionista sei
        </Typography>

        <Grid item xs={12} md={8} sx={{ mt: 1 }}>
          <Typography>
            Seleziona “Persona fisica” se le azioni sono intestate a te
            personalmente oppure “Persona giuridica” se sono intestate a una
            società/ente di cui tu sei il legale rappresentante/amministratore.
          </Typography>
        </Grid>

        {/* PERSON TYPE */}
        <RadioGroup
          name="personType"
          value={delegation.personType || ""}
          onChange={(e) => {
            handleChange(e);
            removeFieldError("personType");
          }}
          sx={{ mt: 1.5 }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              value="NATURAL"
              control={
                <Radio
                  disableRipple
                  checked={delegation.personType === "NATURAL"}
                  sx={{
                    color: "#5b5b5b",
                    "&.Mui-checked": { color: "#5b5b5b" },
                    "&:hover": { backgroundColor: "transparent" },
                    padding: "5px",
                    marginLeft: "2px",
                  }}
                />
              }
              label={
                <>
                  Sono azionista <strong>persona fisica</strong>
                </>
              }
              sx={{
                "& .MuiFormControlLabel-label": {
                  color: errors.includes("personType") ? "#D32F2F" : "#5b5b5b",
                },
              }}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              value="JURIDICAL"
              control={
                <Radio
                  disableRipple
                  checked={delegation.personType === "JURIDICAL"}
                  sx={{
                    color: "#5b5b5b",
                    "&.Mui-checked": { color: "#5b5b5b" },
                    "&:hover": { backgroundColor: "transparent" },
                    padding: "5px",
                    marginLeft: "2px",
                  }}
                />
              }
              label={
                <>
                  Sono azionista <strong>persona giuridica</strong>
                </>
              }
              sx={{
                "& .MuiFormControlLabel-label": {
                  color: errors.includes("personType") ? "#D32F2F" : "#5b5b5b",
                },
              }}
            />
          </Box>
        </RadioGroup>

        <Typography
          sx={{ mt: 2, fontSize: { xs: "16px", md: "18px" }, fontWeight: 600 }}
        >
          Contatti
        </Typography>
        <Typography className="text-steps" sx={{ mt: 0.5 }}>
          Assicurati di inserire dei{" "}
          <span style={{ fontWeight: "bold" }}>contatti validi e attivi</span>{" "}
          in quanto sono determinanti per il corretto rilascio della delega e
          per la sua firma. <br />
          Non è accettato un numero di telefono fisso. Inserire solo un numero
          di <span style={{ fontWeight: "bold" }}>telefono cellulare</span>.
        </Typography>

        {/* EMAIL + CONFIRM */}
        <Grid container sx={{ mt: 1.5 }}>
          {/* EMAIL */}
          <Grid item xs={12} sm={8} md={4} sx={{ position: "relative", mb: 2 }}>
            <Typography className="color-blue-dark title-input">
              Email
            </Typography>

            <TextField
              sx={{ mt: "5px", position: "relative" }}
              placeholder="Inserisci"
              fullWidth
              name="email"
              value={delegation.email || ""}
              onPaste={(e) => e.preventDefault()}
              onChange={(e) => {
                handleChange(e);
                removeFieldError("email");

                if (emailError) handleEmailFormat(e.target.value);
              }}
              onBlur={(e) => handleEmailFormat(e.target.value)}
              InputProps={{
                sx: {
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: emailInputError
                      ? "#D32F2F !important"
                      : "#C4C4C4 !important",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: emailInputError
                      ? "#D32F2F !important"
                      : "#C4C4C4 !important",
                    borderWidth: "1px !important",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: emailInputError ? "#D32F2F" : "#C4C4C4",
                  },
                },
              }}
              error={emailInputError}
              helperText={
                emailError ? "Inserisci un indirizzo e-mail valido" : ""
              }
              FormHelperTextProps={{
                sx: {
                  position: "absolute",
                  bottom: -20,
                  fontSize: "0.8rem",
                  pointerEvents: "none",
                  lineHeight: 1,
                  marginLeft: "0px",
                },
              }}
            />
          </Grid>

          {/* CONFIRM EMAIL */}
          <Grid
            item
            xs={12}
            sm={8}
            md={4}
            sx={{ position: "relative", mb: 3, ml: { xs: 0, md: 2 } }}
          >
            <Typography className="color-blue-dark title-input">
              Conferma email
            </Typography>

            <TextField
              sx={{ mt: "5px" }}
              placeholder="Inserisci"
              fullWidth
              value={confirmEmail}
              onPaste={(e) => e.preventDefault()}
              onChange={(e) => {
                setConfirmEmail(e.target.value);
                setEmailTouched(true);
                removeFieldError("confirm_email");

                if (emailConfirmError) setEmailConfirmError(false);
              }}
              InputProps={{
                sx: {
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: confirmEmailInputError
                      ? "#D32F2F !important"
                      : "#C4C4C4 !important",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: confirmEmailInputError
                      ? "#D32F2F !important"
                      : "#C4C4C4 !important",
                    borderWidth: "1px !important",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: confirmEmailInputError ? "#D32F2F" : "#C4C4C4",
                  },
                },
              }}
              error={confirmEmailInputError}
              helperText={
                emailTouched && emailConfirmError
                  ? "Le e-mail non coincidono"
                  : ""
              }
              FormHelperTextProps={{
                sx: {
                  position: "absolute",
                  bottom: -20,
                  fontSize: "0.8rem",
                  pointerEvents: "none",
                  lineHeight: 1,
                  marginLeft: "0px",
                },
              }}
            />
          </Grid>
        </Grid>

        {/* PHONE + CONFIRM */}
        <Grid container sx={{ mt: 0, mb: { xs: "100px", sm: "120px" } }}>
          {/* PHONE */}
          <Grid item xs={12} sm={8} md={4}>
            <Typography
              className="color-blue-dark title-input"
              sx={{ mb: 0.6 }}
            >
              Numero cellulare
            </Typography>

            <PhoneInput
              country="it"
              enableSearch={true}
              value={delegation.telephone || ""}
              countryCodeEditable={false}
              onChange={handlePhoneChange}
              onFocus={() => setPhoneFocused(true)}
              onBlur={() => setPhoneFocused(false)}
              preferredCountries={["it"]}
              inputProps={{
                placeholder: "Inserisci",
                onPaste: (e) => e.preventDefault(),
              }}
              inputStyle={phoneInputErrorStyle(false)}
              inputClass={
                phoneError || errors.includes("telephone")
                  ? "error-phone-input"
                  : ""
              }
              buttonStyle={{
                borderRadius: "0px",
                border: phoneInputErrorStyle(false).border,
              }}
            />
          </Grid>

          {/* CONFIRM PHONE */}
          <Grid
            item
            xs={12}
            sm={8}
            md={4}
            sx={{ ml: { xs: 0, md: 2 }, mt: { xs: 3, md: 0 } }}
          >
            <Typography
              className="color-blue-dark title-input"
              sx={{ mb: 0.6 }}
            >
              Conferma numero cellulare
            </Typography>

            <Box sx={{ position: "relative" }}>
              <PhoneInput
                country="it"
                enableSearch={true}
                value={confirmTelephone}
                countryCodeEditable={false}
                onChange={(v) => {
                  setConfirmTelephone(v);
                  setPhoneTouched(true);
                  removeFieldError("confirm_telephone");

                  if (phoneError) setPhoneError(false);
                }}
                preferredCountries={["it"]}
                inputProps={{
                  placeholder: "Inserisci",
                  onPaste: (e) => e.preventDefault(),
                }}
                inputStyle={phoneInputErrorStyle(true)}
                inputClass={
                  (phoneTouched && phoneError) ||
                  errors.includes("confirm_telephone")
                    ? "error-phone-input"
                    : ""
                }
                buttonStyle={{
                  borderRadius: "0px",
                  border: phoneInputErrorStyle(true).border,
                }}
              />

              {phoneTouched && phoneError && (
                <Typography
                  color="error"
                  sx={{
                    position: "absolute",
                    bottom: -20,
                    fontSize: "0.8rem",
                    pointerEvents: "none",
                    marginLeft: "0px",
                  }}
                >
                  I numeri non coincidono
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Step_0_Delegation;
